import { useTheme } from '@chakra-ui/react';
import {
  IconButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverArrow,
  PopoverCloseButton,
  Icon,
  Text,
  Tooltip,
  UnorderedList,
  ListItem,
  Flex,
} from '@wegroup/design-system';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconSettingsFilled } from 'wg-fe-ui';
import { Franchise } from '../../../../../../types/Offer';
import {
  isOfferSuccessfull,
  LouiseOffer,
} from '../../../../../../types/Quotes';
import { OfferState } from '../../../services/offersService';
import OfferParametersForm from './OfferParametersForm';

interface Props {
  offer: LouiseOffer;
  disabled?: boolean;
}

const OfferParametersPopover: React.FC<React.PropsWithChildren<Props>> = ({
  offer,
  disabled,
}) => {
  const { colors } = useTheme();
  const { t } = useTranslation();

  const [maxVk, setMaxVk] = useState<number>();
  const [maxCk, setMaxCk] = useState<number>(1);
  const [maxInsuredValue, setMaxInsuredValue] = useState<number>();
  const [maxInsuredContentValue, setMaxInsuredContentValue] =
    useState<number>();
  const [franchises, setFranchises] = useState<Franchise[]>();
  const [intervals, setIntervals] = useState<string[]>();

  /**
   * Parses number to precision 2
   */
  const parseNumber = (number?: number) =>
    number && parseFloat(number.toFixed(2));

  useEffect(() => {
    if (offer.state === OfferState.FETCHED && isOfferSuccessfull(offer)) {
      // Sets the initial values if the quote was ever retrieved
      // If the quote fails this data is not returned so these values are used instead
      setMaxVk(parseNumber(offer.details?.max_vk * 100));
      setMaxCk(parseNumber(offer.details.max_ck * 100) || 100);
      setMaxInsuredValue(parseNumber(offer.details?.insured_value?.max_value));
      setMaxInsuredContentValue(
        parseNumber(offer.details?.insured_content_value?.max_value),
      );
      setFranchises(offer.details?.franchises);
      setIntervals(offer.details.payment_intervals);
    }
  }, [offer]);

  return (
    <Popover>
      {({ onClose }) => (
        <>
          <PopoverTrigger>
            <IconButton
              position="absolute"
              top="2"
              right="2"
              zIndex="1"
              aria-label="Edit offer"
              icon={<IconSettingsFilled color={colors.white} size={25} />}
              size="sm"
              isDisabled={disabled}
            />
          </PopoverTrigger>
          {!disabled && (
            <PopoverContent w="100%">
              <PopoverArrow />
              <PopoverCloseButton />
              <PopoverHeader>
                <Flex alignItems="center">
                  <Text textStyle="normalBodyBold" as="h1" mr="2">
                    {t('Parameters')}
                  </Text>
                  <Tooltip
                    hasArrow
                    shouldWrapChildren
                    label={
                      <OfferDiscountTooltipContent
                        maxCk={maxCk}
                        maxVk={maxVk}
                      />
                    }
                  >
                    <Icon name="GeneralActionsQuestion" />
                  </Tooltip>
                </Flex>
              </PopoverHeader>
              <PopoverBody>
                <OfferParametersForm
                  maxCk={maxCk}
                  maxVk={maxVk}
                  maxInsuredValue={maxInsuredValue}
                  maxInsuredContentValue={maxInsuredContentValue}
                  franchises={franchises}
                  intervals={intervals}
                  offer={offer}
                  onClose={onClose}
                />
              </PopoverBody>
            </PopoverContent>
          )}
        </>
      )}
    </Popover>
  );
};

interface OfferDiscountTooltipContentProps {
  maxVk?: number;
  maxCk: number;
}

const OfferDiscountTooltipContent: React.FC<
  React.PropsWithChildren<OfferDiscountTooltipContentProps>
> = ({ maxVk, maxCk }) => {
  const { t } = useTranslation();

  return (
    <UnorderedList spacing="2">
      <ListItem>
        {t('Proxy Rebate')} ({t('VK')}):{' '}
        {maxVk == null
          ? t('No proxy rebate supported for this quote')
          : `${t('Max')} ${maxVk + '%'}`}
      </ListItem>
      <ListItem>
        {t('Commission Rebate')} ({t('CK')}): {t('Max')} {maxCk + '%'}
      </ListItem>
    </UnorderedList>
  );
};

export default OfferParametersPopover;
